// src/pages/services/Services.js - Estilizado como Notifications
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Grid,
  Alert,
  LinearProgress
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Event as EventIcon,
  School as SchoolIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Euro as EuroIcon,
  Groups as GroupsIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  orderBy, 
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  where
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Paleta de colores verde pastel y violeta metalizado (igual que Notifications)
const colorPalette = {
  primary: '#9CDBA6',        // Verde pastel principal
  primaryDark: '#7FB069',    // Verde pastel oscuro
  primaryLight: '#B8E5C1',   // Verde pastel claro
  secondary: '#9C8AC7',      // Violeta metalizado
  secondaryDark: '#8A7AB8',  // Violeta metalizado oscuro
  secondaryLight: '#B5A8D6', // Violeta metalizado claro
  accent: '#A8E6CF',         // Verde menta
  success: '#81C784',        // Verde éxito
  warning: '#FFB74D',        // Ámbar
  error: '#E57373',          // Rojo suave
  background: '#F8FDF9',     // Fondo verde muy claro
  surface: '#FFFFFF',        // Blanco
  text: '#2E3B3A',          // Verde oscuro para texto
  textSecondary: '#6B7B7A'   // Verde gris para texto secundario
};

// Tema personalizado (igual que Notifications)
const customTheme = createTheme({
  palette: {
    primary: {
      main: colorPalette.primary,
      dark: colorPalette.primaryDark,
      light: colorPalette.primaryLight,
    },
    secondary: {
      main: colorPalette.secondary,
      dark: colorPalette.secondaryDark,
      light: colorPalette.secondaryLight,
    },
    background: {
      default: colorPalette.background,
      paper: colorPalette.surface,
    },
    text: {
      primary: colorPalette.text,
      secondary: colorPalette.textSecondary,
    },
    success: {
      main: colorPalette.success,
    },
    warning: {
      main: colorPalette.warning,
    },
    error: {
      main: colorPalette.error,
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      color: colorPalette.text,
    },
    subtitle2: {
      fontWeight: 500,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colorPalette.surface,
          boxShadow: '0 4px 12px rgba(156, 219, 166, 0.15)',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 4px 12px rgba(156, 138, 199, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(156, 138, 199, 0.4)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(127, 176, 105, 0.12)',
        },
      },
    },
  },
});

// Normaliza los campos de un alumno: acepta las variantes de nombre de campo
// que puedan existir en `children` o incrustadas en la propia inscripción.
const pickChildFields = (child) => ({
  nombre:    child.nombre    || child.name    || '',
  apellidos: child.apellidos || child.apellido || child.surname || '',
  curso:     child.curso     || child.course  || child.nivel || '',
  clase:     child.clase     || child.grupo   || child.aula  || ''
});

// Una inscripción puede referenciar a varios hermanos. Segun como se haya
// escrito el documento (app movil o edicion manual) los hijos pueden venir como:
//   childrenIds: ['id1', 'id2']            -> array de IDs
//   childrenIds: [{ id: 'id1' }, ...]      -> array de objetos con id
//   children:    [{ nombre, curso, ... }]  -> datos ya incrustados
//   childId / childIds / hijos             -> nombres de campo alternativos
//   { 0: 'id1', 1: 'id2' }                 -> mapa en vez de array
const collectChildRefs = (data) => {
  const raw = data.childrenIds ?? data.children ?? data.childIds ?? data.hijos ?? data.childId;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object') return Object.values(raw); // mapa de Firestore
  return [raw];                                           // valor suelto
};

// Resuelve una referencia a alumno en una fila de datos.
const resolveChild = async (ref) => {
  // Ya viene con los datos incrustados en la inscripción
  if (ref && typeof ref === 'object' && (ref.nombre || ref.name || ref.apellidos)) {
    return pickChildFields(ref);
  }

  const childId = typeof ref === 'string'
    ? ref
    : (ref && (ref.id || ref.childId || ref.path?.split('/').pop())) || null;

  if (!childId) return pickChildFields({});

  try {
    const childSnap = await getDoc(doc(db, 'children', childId));
    if (childSnap.exists()) return pickChildFields(childSnap.data());
  } catch (_) {}

  return pickChildFields({});
};

const exportEnrollments = async (service) => {
  try {
    const enrollmentsSnap = await getDocs(
      query(collection(db, 'serviceEnrollments'), where('serviceId', '==', service.id))
    );

    if (enrollmentsSnap.empty) {
      alert('No hay inscritos en este servicio');
      return;
    }

    // Resolver los alumnos de cada inscripción en paralelo
    const nested = await Promise.all(
      enrollmentsSnap.docs.map(async (enrollDoc) => {
        const data = enrollDoc.data();
        const fecha = data.createdAt?.toDate().toLocaleDateString('es-ES') || '';

        let nombrePadre = '';
        let emailPadre = '';
        let telefonoPadre = '';
        if (data.userId) {
          try {
            const userSnap = await getDoc(doc(db, 'users', data.userId));
            if (userSnap.exists()) {
              const user = userSnap.data();
              nombrePadre = `${user.firstName || ''} ${user.lastName || ''}`.trim();
              emailPadre = user.email || '';
              telefonoPadre = user.phone || '';
            }
          } catch (_) {}
        }

        const refs = collectChildRefs(data);

        if (refs.length === 0) {
          console.warn('Inscripción sin hijos identificables:', enrollDoc.id, data);
          return [['', '', '', '', nombrePadre, emailPadre, telefonoPadre, fecha]];
        }

        const children = await Promise.all(refs.map(resolveChild));

        return children.map(c => [c.nombre, c.apellidos, c.curso, c.clase, nombrePadre, emailPadre, telefonoPadre, fecha]);
      })
    );

    // Una fila por alumno: los hermanos de una misma inscripción se separan
    const rows = nested.flat();

    // Ordenar por curso y después por apellidos
    rows.sort((a, b) => {
      if (a[2] !== b[2]) return a[2].localeCompare(b[2]); // curso
      return a[1].localeCompare(b[1]);                     // apellidos
    });

    const csvContent = [
      ['Nombre', 'Apellidos', 'Curso', 'Clase', 'Padre/Madre', 'Email', 'Teléfono', 'Fecha de inscripción'],
      ...rows
    ]
      .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');

    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inscripciones_${service.nombre}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error al exportar inscritos:', error);
    alert('No se pudo exportar la lista');
  }
};

const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [serviceStats, setServiceStats] = useState({});
  const [enrollmentStats, setEnrollmentStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    pending: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [updatingService, setUpdatingService] = useState(null);
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchServices();
    fetchEnrollmentStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, services, showOnlyActive]);

  // Nueva función para obtener estadísticas de inscripciones
  const fetchEnrollmentStats = async () => {
    try {
      console.log('🔄 Obteniendo estadísticas de inscripciones...');
      
      // Obtener todas las inscripciones
      const enrollmentsQuery = query(collection(db, 'serviceEnrollments'));
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      
      let totalEnrollments = 0;
      let activeCount = 0;
      let completedCount = 0;
      let pendingCount = 0;
      
      enrollmentsSnapshot.forEach((doc) => {
        const enrollmentData = doc.data();
        totalEnrollments++;
        
        // Contar por estado
        switch (enrollmentData.status) {
          case 'active':
            activeCount++;
            break;
          case 'completed':
            completedCount++;
            break;
          case 'pending':
            pendingCount++;
            break;
          default:
            activeCount++; // Por defecto consideramos activos
        }
      });
      
      const stats = {
        total: totalEnrollments,
        active: activeCount,
        completed: completedCount,
        pending: pendingCount
      };
      
      console.log('📊 Estadísticas de inscripciones:', stats);
      setEnrollmentStats(stats);
      
    } catch (error) {
      console.error('❌ Error obteniendo estadísticas de inscripciones:', error);
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'services'), orderBy('fechaInicio', 'desc'));
      const querySnapshot = await getDocs(q);
      const servicesData = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        servicesData.push({ 
          id: doc.id, 
          ...data,
          // Convertir timestamps si es necesario
          fechaInicio: data.fechaInicio?.toDate?.() || data.fechaInicio,
          fechaFin: data.fechaFin?.toDate?.() || data.fechaFin,
          createdAt: data.createdAt?.toDate?.() || data.createdAt || new Date()
        });
      });
      
      setServices(servicesData);
      setFilteredServices(servicesData);
      
      // Cargar estadísticas de inscripción para cada servicio
      await fetchServiceStats(servicesData);
      
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceStats = async (servicesData) => {
    try {
      const stats = {};
      
      for (const service of servicesData) {
        // Obtener total de inscripciones para este servicio
        const enrollmentsQuery = query(
          collection(db, 'serviceEnrollments'),
          where('serviceId', '==', service.id)
        );
        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        
        const totalEnrollments = enrollmentsSnapshot.size;
        const maxPlaces = service.plazas || 0;
        const occupancyPercentage = maxPlaces > 0 ? Math.round((totalEnrollments / maxPlaces) * 100) : 0;
        
        stats[service.id] = {
          totalEnrollments,
          maxPlaces,
          availablePlaces: Math.max(0, maxPlaces - totalEnrollments),
          occupancyPercentage,
          isFull: maxPlaces > 0 && totalEnrollments >= maxPlaces
        };
      }
      
      setServiceStats(stats);
    } catch (error) {
      console.error('Error fetching service stats:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchServices(),
      fetchEnrollmentStats()
    ]);
    setRefreshing(false);
  };

  const applyFilters = () => {
    let filtered = [...services];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(service => 
        (service.nombre || '').toLowerCase().includes(term) ||
        (service.descripción || '').toLowerCase().includes(term) ||
        (service.lugar || '').toLowerCase().includes(term) ||
        (service.responsable || '').toLowerCase().includes(term)
      );
    }
    
    // Filter active services
    if (showOnlyActive) {
      filtered = filtered.filter(service => service.active === true);
    }
    
    setFilteredServices(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCreateService = () => {
    navigate('/admin/services/create');
  };

  const handleEditService = (id) => {
    navigate(`/admin/services/${id}`);
  };

  const handleDeleteDialogOpen = (service) => {
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setServiceToDelete(null);
  };

  const handleDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      await deleteDoc(doc(db, 'services', serviceToDelete.id));
      setServices(services.filter(item => item.id !== serviceToDelete.id));
      setDeleteDialogOpen(false);
      setServiceToDelete(null);
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  const handleToggleActive = async (service) => {
    setUpdatingService(service.id);
    try {
      const newActiveStatus = !service.active;
      
      await updateDoc(doc(db, 'services', service.id), {
        active: newActiveStatus
      });
      
      // Update local state
      setServices(services.map(item => 
        item.id === service.id ? { ...item, active: newActiveStatus } : item
      ));
    } catch (error) {
      console.error('Error updating service status:', error);
    } finally {
      setUpdatingService(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Fecha no disponible';
    
    const date = timestamp instanceof Timestamp ? 
      timestamp.toDate() : 
      timestamp instanceof Date ? timestamp :
      new Date(timestamp.seconds * 1000);
      
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  const getOccupancyColor = (percentage) => {
    if (percentage >= 90) return colorPalette.error;
    if (percentage >= 70) return colorPalette.warning;
    return colorPalette.success;
  };

  const getCategoryIcon = (categoria) => {
    const iconProps = { fontSize: 40, mb: 1 };
    switch (categoria) {
      case 'extraescolar':
        return <SchoolIcon sx={{ ...iconProps, color: colorPalette.primary }} />;
      case 'comedor':
        return <GroupsIcon sx={{ ...iconProps, color: colorPalette.secondary }} />;
      case 'transporte':
        return <LocationIcon sx={{ ...iconProps, color: colorPalette.success }} />;
      default:
        return <AssignmentIcon sx={{ ...iconProps, color: colorPalette.accent }} />;
    }
  };

  const totalServices = filteredServices.length;
  const activeServices = filteredServices.filter(s => s.active).length;
  const totalEnrolled = Object.values(serviceStats).reduce((sum, stat) => sum + stat.totalEnrollments, 0);

  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ 
        flexGrow: 1, 
        bgcolor: colorPalette.background, 
        minHeight: '100vh',
        p: 3 
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SchoolIcon sx={{ fontSize: 32, color: colorPalette.primary }} />
            <Typography variant="h4" sx={{ color: colorPalette.text }}>
              Gestión de Servicios
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ color: colorPalette.secondary }}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateService}
              sx={{ 
                bgcolor: colorPalette.secondary,
                '&:hover': { bgcolor: colorPalette.secondaryDark }
              }}
            >
              Nuevo Servicio
            </Button>
          </Box>
        </Box>

        {/* Estadísticas generales */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <SchoolIcon sx={{ fontSize: 40, color: colorPalette.primary, mb: 1 }} />
                <Typography variant="h4" sx={{ color: colorPalette.text, fontWeight: 600 }}>
                  {totalServices}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Servicios
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 40, color: colorPalette.success, mb: 1 }} />
                <Typography variant="h4" sx={{ color: colorPalette.text, fontWeight: 600 }}>
                  {activeServices}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Activos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <PeopleIcon sx={{ fontSize: 40, color: colorPalette.secondary, mb: 1 }} />
                <Typography variant="h4" sx={{ color: colorPalette.text, fontWeight: 600 }}>
                  {enrollmentStats.total}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Inscripciones
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <GroupsIcon sx={{ fontSize: 40, color: colorPalette.accent, mb: 1 }} />
                <Typography variant="h4" sx={{ color: colorPalette.text, fontWeight: 600 }}>
                  {enrollmentStats.active}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Usuarios Activos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Información adicional de inscripciones */}
        {enrollmentStats.total > 0 && (
          <Card sx={{ mb: 4, bgcolor: colorPalette.primaryLight }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{ color: colorPalette.text, mb: 1 }}>
                    🎓 Inscripciones en Servicios
                  </Typography>
                  <Typography variant="body2" sx={{ color: colorPalette.textSecondary }}>
                    Total de {enrollmentStats.total} inscripciones registradas.
                    {enrollmentStats.pending > 0 && ` Hay ${enrollmentStats.pending} inscripciones pendientes.`}
                  </Typography>
                </Grid>
                <Grid item xs={8} md={6} sx={{ display: 'flex', flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CheckCircleIcon sx={{ color: colorPalette.success, fontSize: 24 }} />
                      <Typography variant="caption" display="block">
                        {enrollmentStats.active} Activas
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <ScheduleIcon sx={{ color: colorPalette.warning, fontSize: 24 }} />
                      <Typography variant="caption" display="block">
                        {enrollmentStats.pending} Pendientes
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <AssignmentIcon sx={{ color: colorPalette.secondary, fontSize: 24 }} />
                      <Typography variant="caption" display="block">
                        {enrollmentStats.completed} Completadas
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Tabla de servicios */}
        <Paper sx={{ width: '100%', mb: 2 }}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por nombre, descripción, lugar o responsable..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: colorPalette.primary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: colorPalette.primary,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: colorPalette.secondary,
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showOnlyActive}
                      onChange={(e) => setShowOnlyActive(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Solo servicios activos"
                  sx={{ 
                    ml: 2,
                    '& .MuiFormControlLabel-label': {
                      color: colorPalette.text,
                      fontWeight: 500
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: colorPalette.primaryLight }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Servicio
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Fechas
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Lugar
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Precio
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Ocupación
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Estado
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 4 }}>
                        <CircularProgress sx={{ color: colorPalette.primary }} />
                        <Typography variant="body2" sx={{ mt: 2, color: colorPalette.textSecondary }}>
                          Cargando servicios...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredServices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 4 }}>
                        <SchoolIcon sx={{ fontSize: 48, color: colorPalette.textSecondary, mb: 2 }} />
                        <Typography variant="body1" sx={{ color: colorPalette.textSecondary }}>
                          {searchTerm ? 'No se encontraron servicios' : 'No hay servicios aún'}
                        </Typography>
                        {!searchTerm && (
                          <Button 
                            variant="contained" 
                            onClick={handleCreateService}
                            sx={{ mt: 2, bgcolor: colorPalette.primary }}
                          >
                            Crear primer servicio
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredServices
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((service) => {
                      const stats = serviceStats[service.id] || {
                        totalEnrollments: 0,
                        maxPlaces: 0,
                        availablePlaces: 0,
                        occupancyPercentage: 0,
                        isFull: false
                      };

                      return (
                        <TableRow 
                          key={service.id}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: colorPalette.background,
                              transition: 'background-color 0.2s ease'
                            }
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                {service.nombre}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="textSecondary" 
                                sx={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  maxWidth: 200,
                                  mb: 0.5
                                }}
                              >
                                {service.descripción || 'Sin descripción'}
                              </Typography>
                              {service.responsable && (
                                <Typography variant="caption" color="textSecondary">
                                  Responsable: {service.responsable}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <EventIcon sx={{ mr: 1, color: colorPalette.textSecondary, fontSize: 'small' }} />
                              <Box>
                                <Typography variant="body2">
                                  Inicio: {formatDate(service.fechaInicio)}
                                </Typography>
                                <Typography variant="body2">
                                  Fin: {formatDate(service.fechaFin)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <LocationIcon sx={{ mr: 1, color: colorPalette.textSecondary, fontSize: 'small' }} />
                              <Typography variant="body2">
                                {service.lugar || 'No especificado'}
                              </Typography>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <EuroIcon sx={{ mr: 0.5, color: colorPalette.success, fontSize: 'small' }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {service.precio ? `${service.precio.toFixed(2)} €` : 'Gratis'}
                              </Typography>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PeopleIcon sx={{ fontSize: 16, color: colorPalette.textSecondary }} />
                                <Typography variant="body2">
                                  {stats.totalEnrollments}/{stats.maxPlaces || '∞'}
                                </Typography>
                                <Chip 
                                  label={`${stats.occupancyPercentage}%`}
                                  size="small"
                                  sx={{ 
                                    bgcolor: getOccupancyColor(stats.occupancyPercentage),
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: 11
                                  }}
                                />
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={stats.occupancyPercentage} 
                                sx={{ 
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: 'rgba(0,0,0,0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: getOccupancyColor(stats.occupancyPercentage),
                                    borderRadius: 3,
                                  }
                                }}
                              />
                              {stats.availablePlaces === 0 && stats.maxPlaces > 0 ? (
                                <Chip 
                                  size="small" 
                                  color="error" 
                                  label="Completo" 
                                  sx={{ mt: 0.5, fontSize: 10 }}
                                />
                              ) : stats.availablePlaces <= 5 && stats.availablePlaces > 0 ? (
                                <Chip 
                                  size="small" 
                                  color="warning" 
                                  label="Últimas plazas" 
                                  sx={{ mt: 0.5, fontSize: 10 }}
                                />
                              ) : null}
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={service.active || false}
                                    onChange={() => handleToggleActive(service)}
                                    disabled={updatingService === service.id}
                                    size="small"
                                  />
                                }
                                label={service.active ? "Activo" : "Inactivo"}
                                sx={{
                                  '& .MuiFormControlLabel-label': {
                                    fontSize: 12,
                                    color: service.active ? colorPalette.success : colorPalette.textSecondary
                                  }
                                }}
                              />
                            </Box>
                          </TableCell>
                          
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                              <Tooltip title="Exportar inscritos">
                                <IconButton
                                  onClick={() => exportEnrollments(service)}
                                  size="small"
                                  sx={{ color: colorPalette.primary }}
                                >
                                  <EventIcon />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Editar servicio">
                                <IconButton 
                                  onClick={() => handleEditService(service.id)}
                                  size="small"
                                  sx={{ color: colorPalette.secondary }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Eliminar servicio">
                                <IconButton 
                                  onClick={() => handleDeleteDialogOpen(service)}
                                  size="small"
                                  sx={{ color: colorPalette.error }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredServices.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              '& .MuiTablePagination-toolbar': {
                bgcolor: colorPalette.background,
              },
            }}
          />
        </Paper>

        {/* Diálogo de confirmación de eliminación */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: colorPalette.surface,
            }
          }}
        >
          <DialogTitle sx={{ color: colorPalette.text, fontWeight: 600 }}>
            ¿Eliminar servicio?
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Esta acción no se puede deshacer
            </Alert>
            <DialogContentText sx={{ color: colorPalette.textSecondary }}>
              ¿Estás seguro de que quieres eliminar el servicio{' '}
              <strong>"{serviceToDelete?.nombre}"</strong>?
              {serviceStats[serviceToDelete?.id]?.totalEnrollments > 0 && (
                <Typography variant="body2" sx={{ mt: 1, color: colorPalette.error }}>
                  Este servicio tiene {serviceStats[serviceToDelete?.id]?.totalEnrollments} inscripciones.
                </Typography>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={handleDeleteDialogClose}
              variant="outlined"
              sx={{ color: colorPalette.textSecondary }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteService} 
              variant="contained"
              color="error"
              autoFocus
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Alertas informativas */}
        {filteredServices.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {services.some(s => serviceStats[s.id]?.isFull) && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: colorPalette.warning }
                }}
              >
                Algunos servicios están completos. Los usuarios no podrán inscribirse.
              </Alert>
            )}
            
            {services.some(s => !s.active) && showOnlyActive && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: colorPalette.secondary }
                }}
              >
                Hay servicios inactivos que no se muestran. Desactiva el filtro para verlos.
              </Alert>
            )}

            {enrollmentStats.total === 0 && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: colorPalette.secondary }
                }}
              >
                <Typography variant="body2">
                  <strong>No hay inscripciones aún.</strong><br />
                  Los usuarios podrán inscribirse a los servicios desde la aplicación móvil.
                </Typography>
              </Alert>
            )}

            {enrollmentStats.pending > 0 && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: colorPalette.warning }
                }}
              >
                <Typography variant="body2">
                  Hay {enrollmentStats.pending} inscripciones pendientes de aprobación.
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Información adicional sobre inscripciones */}
        {enrollmentStats.total > 0 && (
          <Card sx={{ mt: 2, bgcolor: colorPalette.background }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: colorPalette.text }}>
                📊 Resumen de Inscripciones
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Distribución por estado:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {enrollmentStats.active > 0 && (
                      <Chip 
                        icon={<CheckCircleIcon />}
                        label={`Activas: ${enrollmentStats.active}`}
                        sx={{ bgcolor: '#E8F5E8', color: '#2E7D32' }}
                      />
                    )}
                    {enrollmentStats.pending > 0 && (
                      <Chip 
                        icon={<ScheduleIcon />}
                        label={`Pendientes: ${enrollmentStats.pending}`}
                        sx={{ bgcolor: '#FFF3E0', color: '#F57C00' }}
                      />
                    )}
                    {enrollmentStats.completed > 0 && (
                      <Chip 
                        icon={<AssignmentIcon />}
                        label={`Completadas: ${enrollmentStats.completed}`}
                        sx={{ bgcolor: colorPalette.secondaryLight, color: colorPalette.secondaryDark }}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Servicios más populares:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {Object.entries(serviceStats)
                      .sort((a, b) => b[1].totalEnrollments - a[1].totalEnrollments)
                      .slice(0, 3)
                      .map(([serviceId, stats], index) => {
                        const service = services.find(s => s.id === serviceId);
                        return service ? (
                          <Typography key={serviceId} variant="caption" sx={{ color: colorPalette.textSecondary }}>
                            {index + 1}. {service.nombre} ({stats.totalEnrollments} inscripciones)
                          </Typography>
                        ) : null;
                      })}
                  </Box>
                </Grid>
              </Grid>
              <Typography variant="caption" sx={{ mt: 2, display: 'block', color: colorPalette.textSecondary }}>
                Las inscripciones se registran automáticamente cuando los usuarios se apuntan desde la aplicación móvil.
                Puedes exportar las listas de inscritos usando el botón de exportar en cada servicio.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default Services;